import { parseArticleId } from "./article-id";

export type SavedArticle = { id: string; title: string; url: string; host: string };
export const SAVED_ARTICLES_LIMIT = 100;
const STORAGE_KEY = "beat-saved-articles";
const CHANGE_EVENT = "beat-saved-articles-change";
const EMPTY_SNAPSHOT = "[]";

/** 保存候補から必要な公開情報だけを返す。不正なID・URL・空文字はnull。副作用・例外なし。 */
function validateArticle(value: unknown): SavedArticle | null {
	if (!value || typeof value !== "object") return null;
	const item = value as Record<string, unknown>;
	const articleId = parseArticleId(item.id);
	if (
		articleId === null ||
		typeof item.title !== "string" ||
		!item.title.trim() ||
		item.title.length > 1000 ||
		typeof item.host !== "string" ||
		!item.host.trim() ||
		item.host.length > 200 ||
		typeof item.url !== "string" ||
		item.url.length > 4096
	)
		return null;
	try {
		const url = new URL(item.url);
		if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) return null;
		return {
			id: articleId.toString(),
			title: item.title.trim(),
			host: item.host.trim(),
			url: url.href,
		};
	} catch {
		return null;
	}
}

/** 保存JSONを検証し、新しい順の最大100件を返す。同一ID・URLは先頭を優先。破損時は空配列。副作用なし。 */
export function parseSavedArticles(raw: string): SavedArticle[] {
	try {
		const values: unknown = JSON.parse(raw);
		if (!Array.isArray(values)) return [];
		const articles: SavedArticle[] = [];
		const ids = new Set<string>();
		const urls = new Set<string>();
		for (const value of values) {
			const article = validateArticle(value);
			if (!article || ids.has(article.id) || urls.has(article.url)) continue;
			articles.push(article);
			ids.add(article.id);
			urls.add(article.url);
			if (articles.length === SAVED_ARTICLES_LIMIT) break;
		}
		return articles;
	} catch {
		return [];
	}
}

/** 現在の保存JSONと記事から切替後の配列を返す。不正な候補はnull。追加時は最古の101件目を除く。副作用なし。 */
export function toggleSavedArticleRecords(
	raw: string,
	candidate: SavedArticle,
): SavedArticle[] | null {
	const article = validateArticle(candidate);
	if (!article) return null;
	const current = parseSavedArticles(raw);
	const exists = current.some((item) => item.id === article.id || item.url === article.url);
	return exists
		? current.filter((item) => item.id !== article.id && item.url !== article.url)
		: [article, ...current].slice(0, SAVED_ARTICLES_LIMIT);
}

/** 引数なし。ブラウザー保存値を安定した文字列として返す。SSR・保存拒否時は空JSON。例外を外に出さない。 */
export function getSavedArticlesSnapshot(): string {
	try {
		return typeof window === "undefined"
			? EMPTY_SNAPSHOT
			: (window.localStorage.getItem(STORAGE_KEY) ?? EMPTY_SNAPSHOT);
	} catch {
		return EMPTY_SNAPSHOT;
	}
}

/** 引数なし。SSRの決定的な空スナップショットを返す。副作用・例外なし。 */
export function getSavedArticlesServerSnapshot(): string {
	return EMPTY_SNAPSHOT;
}

/** コールバックを同一タブ・他タブの保存変更へ購読し、解除関数を返す。ブラウザーでのみ使用。 */
export function subscribeSavedArticles(onChange: () => void): () => void {
	/** 対象キーの変更・全消去だけを通知する。入力はブラウザーのstorageイベント。 */
	function onStorage(event: StorageEvent) {
		if (event.key === STORAGE_KEY || event.key === null) onChange();
	}
	window.addEventListener("storage", onStorage);
	window.addEventListener(CHANGE_EVENT, onChange);
	return () => {
		window.removeEventListener("storage", onStorage);
		window.removeEventListener(CHANGE_EVENT, onChange);
	};
}

/** 記事の保存状態を切り替え、成功可否を返す。localStorageと同一タブへ通知する。保存拒否・容量超過はfalse。 */
export function toggleSavedArticle(article: SavedArticle): boolean {
	if (typeof window === "undefined") return false;
	try {
		const records = toggleSavedArticleRecords(
			window.localStorage.getItem(STORAGE_KEY) ?? EMPTY_SNAPSHOT,
			article,
		);
		if (!records) return false;
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
		window.dispatchEvent(new Event(CHANGE_EVENT));
		return true;
	} catch {
		return false;
	}
}
